import { useState } from "react";
import { MessageSquare, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { addLeadNote } from "@/lib/api";
import { shortDate } from "@/lib/format";

// Lead notes — same pattern as the Proposals pipeline: last 3 inline as amber chips, popover for the
// full log + add box. Source is `lead.notes` ([{date, text}]), appended via POST /leads/{id}/notes.
export const leadNotes = (lead) => {
  const n = lead?.notes;
  if (Array.isArray(n)) return n;
  if (typeof n === "string" && n.trim()) return [{ date: "", text: n }];
  return [];
};

export function LeadNotesCell({ lead, onChanged }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const notes = leadNotes(lead);

  const add = async () => {
    if (!text.trim() || saving) return;
    setSaving(true);
    try {
      await addLeadNote(lead.id, text.trim());
      setText("");
      onChanged?.();
    } catch { toast.error("Failed to add note"); }
    finally { setSaving(false); }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        {notes.length > 0 ? (
          <button type="button" title={`${notes.length} note${notes.length === 1 ? "" : "s"} — read or add`}
            className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors"
            data-testid={`lead-notes-${lead.id}`}>
            <MessageSquare className="w-3.5 h-3.5" />
            <span className="num" data-testid={`lead-notes-count-${lead.id}`}>{notes.length}</span>
          </button>
        ) : (
          <button type="button" title="Add the first note"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            data-testid={`lead-notes-${lead.id}`}>
            <Plus className="w-4 h-4" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="center" onClick={(e) => e.stopPropagation()}>
        <div className="text-[11px] text-slate-500 font-semibold mb-2">Notes</div>
        <div className="max-h-40 overflow-y-auto space-y-1 mb-2">
          {notes.length === 0 && <div className="text-[10px] text-slate-300 italic py-2 text-center">No notes yet</div>}
          {[...notes].reverse().map((n, i) => (
            // eslint-disable-next-line react/no-array-index-key -- per-lead note log
            <div key={`note-${i}-${n.date || ""}`} className="text-xs bg-slate-50 rounded px-2 py-1.5" data-testid={`lead-note-${lead.id}-${i}`}>
              {n.date && <span className="text-slate-400 text-[10px] font-medium">{shortDate(n.date)}</span>}
              <span className={n.date ? "ml-1.5 text-slate-700" : "text-slate-700"}>{n.text}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-1">
          <Input
            className="h-7 text-xs flex-1"
            placeholder="Add a note..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            data-testid={`lead-note-input-${lead.id}`}
          />
          <Button size="sm" className="h-7 text-xs px-2" onClick={add} disabled={saving} data-testid={`lead-note-add-${lead.id}`}>Add</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
